import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Scan, LogOut, Activity, AlertTriangle, Timer, Gauge, PlayCircle, MessagesSquare, Inbox, Send, X, Loader2 } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Textarea } from "../components/ui/textarea";

const MODALITY_COLORS = { CT: "#2563EB", MRI: "#0D9488", "X-RAY": "#0F172A", ULTRASOUND: "#94A3B8" };

const tooltipStyle = {
  contentStyle: { background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 },
  labelStyle: { color: "#8A8A8E" },
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scans, setScans] = useState(null);
  const [tab, setTab] = useState("overview");
  const [contacts, setContacts] = useState(null);

  useEffect(() => {
    api.get("/scans").then((r) => setScans(r.data.scans)).catch(() => setScans([]));
  }, []);

  useEffect(() => {
    if (tab === "enquiries" && contacts === null) {
      api.get("/contacts").then((r) => setContacts(r.data.contacts)).catch(() => setContacts([]));
    }
  }, [tab, contacts]);

  const [analytics, setAnalytics] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/analytics/summary").then((r) => setAnalytics(r.data)).catch(() => {});
    }
  }, [user]);

  const sendReply = async (id) => {
    if (!replyText.trim() || replySending) return;
    setReplySending(true);
    try {
      await api.post(`/contacts/${id}/reply`, { message: replyText });
      toast.success("Reply emailed to the enquirer.");
      setReplyTo(null);
      setReplyText("");
      setContacts(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReplySending(false);
    }
  };

  const stats = useMemo(() => {
    if (!scans || !scans.length) return null;
    const flagged = scans.filter((s) => s.status === "Flagged").length;
    const avgConf = scans.reduce((a, s) => a + s.confidence, 0) / scans.length;
    const avgMs = scans.reduce((a, s) => a + s.processing_ms, 0) / scans.length;
    const byDay = {};
    for (const s of scans) {
      const day = s.created_at.slice(5, 10);
      byDay[day] = (byDay[day] || 0) + 1;
    }
    const trend = Object.entries(byDay).sort().map(([day, count]) => ({ day, scans: count }));
    const byMod = {};
    for (const s of scans) byMod[s.modality] = (byMod[s.modality] || 0) + 1;
    const modalities = Object.entries(byMod).map(([name, count]) => ({ name, count }));
    return { total: scans.length, flagged, avgConf, avgMs, trend, modalities };
  }, [scans]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] md:flex" data-testid="dashboard-page">
      <aside className="hidden md:flex w-64 shrink-0 bg-white border-r border-slate-200 min-h-screen sticky top-0 flex-col" data-testid="dashboard-sidebar">
        <Link to="/" className="flex items-center gap-2 px-6 h-16 border-b border-slate-200" data-testid="dashboard-logo">
          <span className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
            <Scan className="w-4 h-4 text-white" />
          </span>
          <span className="font-display font-extrabold tracking-tight">X-GEN.AI</span>
        </Link>
        <nav className="flex-1 p-4 space-y-1">
          <button
            onClick={() => setTab("overview")}
            data-testid="sidebar-nav-overview"
            className={`w-full text-left font-mono text-xs uppercase tracking-[0.15em] px-4 py-3 rounded-full transition-colors duration-300 ${tab === "overview" ? "bg-brand text-white" : "text-mute hover:bg-slate-50"}`}
          >
            Overview
          </button>
          {user?.role === "admin" && (
            <button
              onClick={() => setTab("enquiries")}
              data-testid="sidebar-nav-enquiries"
              className={`w-full text-left font-mono text-xs uppercase tracking-[0.15em] px-4 py-3 rounded-full transition-colors duration-300 ${tab === "enquiries" ? "bg-brand text-white" : "text-mute hover:bg-slate-50"}`}
            >
              Enquiries
            </button>
          )}
          <Link
            to="/"
            data-testid="sidebar-nav-site"
            className="block font-mono text-xs uppercase tracking-[0.15em] px-4 py-3 text-mute hover:bg-slate-50 rounded-full transition-colors duration-300"
          >
            View site
          </Link>
        </nav>
        <div className="p-4 border-t border-slate-200">
          <p className="font-mono text-[10px] text-mute truncate mb-3" data-testid="dashboard-user-email">{user?.email}</p>
          <button
            onClick={handleLogout}
            data-testid="dashboard-logout-button"
            className="w-full flex items-center justify-center gap-2 border border-slate-200 font-mono text-xs uppercase tracking-[0.15em] px-4 py-2.5 text-mute hover:border-pulse hover:text-pulse transition-colors duration-300"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="md:hidden sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200 px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo-mobile">
            <span className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
              <Scan className="w-4 h-4 text-white" />
            </span>
            <span className="font-display font-extrabold tracking-tight">X-GEN.AI</span>
          </Link>
          <button onClick={handleLogout} data-testid="dashboard-logout-button-mobile" className="font-mono text-xs uppercase tracking-[0.15em] text-mute">
            Sign out
          </button>
        </header>
        <main className="p-6 md:p-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-10">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-2">Worklist overview</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Welcome, {user?.name}
          </h1>
        </motion.div>

        {user?.role === "admin" && analytics && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="analytics-strip">
            {[
              { icon: PlayCircle, label: "Demo runs (7d)", value: analytics.demo_runs, testid: "analytics-demo-runs" },
              { icon: MessagesSquare, label: "Chat sessions (7d)", value: analytics.chat_sessions, testid: "analytics-chat-sessions" },
              { icon: Inbox, label: "Enquiries (7d)", value: analytics.enquiries, testid: "analytics-enquiries" },
            ].map((a) => (
              <div key={a.label} className="bg-surface border border-slate-200 rounded-2xl px-5 py-4 flex items-center gap-4" data-testid={a.testid}>
                <a.icon className="w-4 h-4 text-brand shrink-0" />
                <span className="font-display text-2xl font-bold">{a.value}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute ml-auto text-right">{a.label}</span>
              </div>
            ))}
          </div>
        )}

        {tab === "overview" && (!stats ? (
          <div className="flex items-center justify-center py-32" data-testid="dashboard-loading">
            <div className="w-8 h-8 border-2 border-slate-300 border-t-brand animate-spin rounded-full" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="dashboard-grid">
            {[
              { icon: Activity, label: "Studies analyzed", value: stats.total, testid: "stat-total" },
              { icon: AlertTriangle, label: "Oncology flags", value: stats.flagged, accent: true, testid: "stat-flagged" },
              { icon: Gauge, label: "Mean confidence", value: `${(stats.avgConf * 100).toFixed(1)}%`, testid: "stat-confidence" },
              { icon: Timer, label: "Mean inference", value: `${(stats.avgMs / 1000).toFixed(1)}s`, testid: "stat-speed" },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="bg-surface border border-slate-200 rounded-2xl p-6"
                data-testid={s.testid}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute">{s.label}</span>
                  <s.icon className={`w-4 h-4 ${s.accent ? "text-pulse" : "text-brand"}`} />
                </div>
                <div className={`font-display text-4xl font-bold ${s.accent ? "text-pulse" : ""}`}>{s.value}</div>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-surface border border-slate-200 rounded-2xl p-6 md:col-span-2 lg:col-span-3"
              data-testid="chart-scan-trend"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute mb-6">Scan volume — last 14 days</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.trend} margin={{ top: 0, right: 0, left: -24, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(15,23,42,0.06)" vertical={false} />
                    <XAxis dataKey="day" tick={{ fill: "#8A8A8E", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#8A8A8E", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip {...tooltipStyle} />
                    <Area type="stepAfter" dataKey="scans" stroke="#2563EB" strokeWidth={2} fill="#2563EB" fillOpacity={0.1} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.38 }}
              className="bg-surface border border-slate-200 rounded-2xl p-6"
              data-testid="chart-modality-split"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute mb-6">Modality split</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.modalities} margin={{ top: 0, right: 0, left: -28, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(15,23,42,0.06)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: "#8A8A8E", fontSize: 9, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#8A8A8E", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip {...tooltipStyle} cursor={{ fill: "rgba(15,23,42,0.04)" }} />
                    <Bar dataKey="count">
                      {stats.modalities.map((m) => (
                        <Cell key={m.name} fill={MODALITY_COLORS[m.name] || "#2563EB"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.46 }}
              className="bg-surface border border-slate-200 rounded-2xl md:col-span-3 lg:col-span-4 overflow-hidden"
              data-testid="scans-table"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute">Recent studies</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">AI-analyzed</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.2em] text-mute">
                      <th className="text-left px-6 py-3 font-medium">Patient</th>
                      <th className="text-left px-4 py-3 font-medium">Modality</th>
                      <th className="text-left px-4 py-3 font-medium">Finding</th>
                      <th className="text-left px-4 py-3 font-medium">Conf.</th>
                      <th className="text-left px-6 py-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scans.map((s) => (
                      <tr key={s.id} className="border-b border-slate-200 hover:bg-slate-50 transition-colors duration-200" data-testid={`scan-row-${s.patient_ref}`}>
                        <td className="px-6 py-4 font-mono text-xs">{s.patient_ref}</td>
                        <td className="px-4 py-4 font-mono text-xs text-mute">{s.modality} · {s.region}</td>
                        <td className="px-4 py-4 text-slate-600 text-xs max-w-[280px]">{s.finding}</td>
                        <td className="px-4 py-4 font-mono text-xs">{(s.confidence * 100).toFixed(1)}%</td>
                        <td className="px-6 py-4">
                          <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${s.status === "Flagged" ? "border-pulse/50 text-pulse bg-pulse/10" : "border-brand/40 text-brand bg-brand/10"}`}>
                            {s.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        ))}

        {tab === "enquiries" && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-surface border border-slate-200 rounded-2xl overflow-hidden"
            data-testid="enquiries-table"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute">Pilot enquiries</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">
                {contacts ? `${contacts.length} received` : "..."}
              </p>
            </div>
            {!contacts ? (
              <div className="flex items-center justify-center py-24" data-testid="enquiries-loading">
                <div className="w-8 h-8 border-2 border-slate-300 border-t-brand animate-spin rounded-full" />
              </div>
            ) : contacts.length === 0 ? (
              <p className="px-6 py-16 text-center text-mute text-sm" data-testid="enquiries-empty">No enquiries yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.2em] text-mute">
                      <th className="text-left px-6 py-3 font-medium">Name</th>
                      <th className="text-left px-4 py-3 font-medium">Email</th>
                      <th className="text-left px-4 py-3 font-medium">Organization</th>
                      <th className="text-left px-4 py-3 font-medium">Message</th>
                      <th className="text-left px-6 py-3 font-medium">Received</th>
                      <th className="text-right px-6 py-3 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((item) => (
                      <Fragment key={item.id}>
                        <tr className="border-b border-slate-200 hover:bg-slate-50 transition-colors duration-200" data-testid={`enquiry-row-${item.id}`}>
                          <td className="px-6 py-4 text-slate-700 text-xs">{item.name}</td>
                          <td className="px-4 py-4 font-mono text-xs text-brand">{item.email}</td>
                          <td className="px-4 py-4 text-slate-600 text-xs">{item.organization || "—"}</td>
                          <td className="px-4 py-4 text-mute text-xs max-w-[320px] truncate">{item.message}</td>
                          <td className="px-6 py-4 font-mono text-xs text-mute">
                            {item.created_at.slice(0, 10)}
                            {item.replies?.length > 0 && (
                              <span className="block text-brand mt-1">
                                {item.replies.length} {item.replies.length === 1 ? "reply" : "replies"} sent
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => { setReplyTo(replyTo === item.id ? null : item.id); setReplyText(""); }}
                              data-testid={`enquiry-reply-button-${item.id}`}
                              className="font-mono text-[10px] uppercase tracking-[0.15em] border border-slate-200 px-3 py-1.5 text-mute hover:border-brand hover:text-slate-900 transition-colors duration-300"
                            >
                              Reply
                            </button>
                          </td>
                        </tr>
                        {replyTo === item.id && (
                          <tr className="border-b border-slate-200 bg-slate-50" data-testid={`enquiry-reply-panel-${item.id}`}>
                            <td colSpan={6} className="px-6 py-5">
                              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute mb-3">
                                Reply to {item.name} — {item.email}
                              </p>
                              <Textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Write your reply... it will be emailed from X-GEN.AI"
                                rows={4}
                                data-testid={`enquiry-reply-textarea-${item.id}`}
                                className="bg-ink border-slate-200 rounded-lg focus-visible:ring-brand resize-none mb-3"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => sendReply(item.id)}
                                  disabled={replySending || !replyText.trim()}
                                  data-testid={`enquiry-reply-send-${item.id}`}
                                  className="flex items-center gap-2 bg-brand text-white font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 hover:bg-[#0F172A] hover:text-white transition-colors duration-300 disabled:opacity-50"
                                >
                                  {replySending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                  Send reply
                                </button>
                                <button
                                  onClick={() => setReplyTo(null)}
                                  data-testid={`enquiry-reply-cancel-${item.id}`}
                                  className="flex items-center gap-2 border border-slate-200 font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 text-mute hover:text-slate-900 transition-colors duration-300"
                                >
                                  <X className="w-3 h-3" /> Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}

        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-mute" data-testid="dashboard-disclaimer">
          Decision-support only — final diagnosis rests with the attending radiologist.
        </p>
        </main>
      </div>
    </div>
  );
}
